/**
 * Timeline View
 * 
 * Displays a chronological timeline of a user's career journey, showing work experiences,
 * career cards, notes, reviews, and resumes. This implementation uses an optimized
 * approach with consolidated state management and efficient rendering to avoid
 * unnecessary recalculations and UI flashing.
 */

import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { Group } from '@visx/group';
import { format } from 'date-fns';
import { debounce } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { ResizeObserver } from '@juggle/resize-observer';
import { Range, getTrackBackground } from 'react-range';
import { useTimelineState } from '../hooks/useTimelineState';
import PageHeader from '../components/PageHeader';
import TimelineItem from '../components/Timeline/Item';
import TimelineGrid from '../components/Timeline/Grid';
import TimelineTooltip from '../components/Timeline/Tooltip';
import TimelineControls from '../components/Timeline/Controls';
import TimelineLegend from '../components/Timeline/Legend';
import type { TimelineItem as TimelineItemType, TimelineItemType as ItemType, HoverPosition } from '../types/timeline';

const TimelineView = () => {
    const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  
  // Use the timeline state hook to manage all timeline state
  const timeline = useTimelineState();
  const [hoveredItemData, setHoveredItemData] = useState<{
    item: TimelineItemType | null;
    position: HoverPosition | null;
  }>({ item: null, position: null });
  
  // Range slider state
  const [timelineRange, setTimelineRange] = useState<[number, number]>([0, 100]);
  
  // Window lock state
  const [isWindowLocked, setIsWindowLocked] = useState(false);
  const [windowSizeMs, setWindowSizeMs] = useState<number | null>(null);
  
  // Check if zoom is at minimum (0)
  const isMinZoom = useMemo(() => timeline.viewState.zoomLevel === 0, [timeline.viewState.zoomLevel]);
  
  // If zoom level is reset to min, ensure window lock is disabled
  useEffect(() => {
    if (isMinZoom && isWindowLocked) {
      setIsWindowLocked(false);
      setWindowSizeMs(null);
    }
  }, [isMinZoom, isWindowLocked]);
  
  // Calculate Y positions for different item types
  const calculateYPosition = useCallback((index: number, type: ItemType) => {
    const ROW_HEIGHT = 45;
    
    if (type === 'workExperience') {
      // Work experiences start from row 1 (after top section)
      return 60 + index * ROW_HEIGHT;
            } else {
      // All other items go in the top section
      // When work experiences are hidden, position top items closer to the timeline axis
      const isWorkExperienceVisible = timeline.visibleTypes.has('workExperience');
      return isWorkExperienceVisible ? 20 : 60; // Align with first row position when no work experiences
    }
  }, [timeline.visibleTypes]);

  // Calculate the total height needed based on the number of work experiences
  const totalContentHeight = useMemo(() => {
    const ROW_HEIGHT = 45;
    const MIN_ROWS = 1;
    const HEADER_FOOTER_HEIGHT = 180; // Combined height for header, axis, and additional padding
    
    // If work experience type is visible in the legend, use work experience count
    const isWorkExperienceVisible = timeline.visibleTypes.has('workExperience');
    const visibleRowCount = isWorkExperienceVisible 
      ? timeline.visibleData.workExperiences.length 
      : MIN_ROWS;
    
    // Ensure we have at least one row, even when all work experiences are filtered out
    const effectiveRowCount = Math.max(visibleRowCount, MIN_ROWS);
    
    // Calculate final height with a minimum requirement
    return Math.max(
      effectiveRowCount * ROW_HEIGHT + HEADER_FOOTER_HEIGHT,
      200 // Absolute minimum height even with no items
    );
  }, [timeline.visibleData.workExperiences.length, timeline.visibleTypes]);

  // Handle container resize
    useEffect(() => {
    if (!containerRef.current) return;

    const debouncedResize = debounce(() => {
        if (containerRef.current) {
        timeline.setDimensions({
          containerWidth: containerRef.current.offsetWidth,
          containerHeight: containerRef.current.offsetHeight,
          contentHeight: totalContentHeight
        });
      }
    }, 100);
    
    const resizeObserver = new ResizeObserver(debouncedResize);
    resizeObserver.observe(containerRef.current);
    debouncedResize(); // Initial size calculation
        
        return () => {
      resizeObserver.disconnect();
      debouncedResize.cancel();
    };
  }, [timeline, totalContentHeight]);

  // Update timeline range slider when visible time range changes
    useEffect(() => {
    if (timeline.timelineBounds.start && timeline.timelineBounds.end) {
      const totalDuration = timeline.timelineBounds.end.getTime() - timeline.timelineBounds.start.getTime();
      if (totalDuration > 0) {
        const startPercentage = ((timeline.viewState.visibleTimeRange.start.getTime() - timeline.timelineBounds.start.getTime()) / totalDuration) * 100;
        const endPercentage = ((timeline.viewState.visibleTimeRange.end.getTime() - timeline.timelineBounds.start.getTime()) / totalDuration) * 100;
        setTimelineRange([
          Math.round(startPercentage * 10) / 10,
          Math.round(endPercentage * 10) / 10
        ]);
        
        // Store the window size when locking
        if (isWindowLocked && windowSizeMs === null) {
          setWindowSizeMs(timeline.viewState.visibleTimeRange.end.getTime() - timeline.viewState.visibleTimeRange.start.getTime());
        }
      }
    }
  }, [timeline.viewState.visibleTimeRange, timeline.timelineBounds, isWindowLocked]);

  // Handle timeline range slider change
  const handleTimelineRangeChange = useCallback((values: number[]) => {
        const [start, end] = values;
    const totalDuration = timeline.timelineBounds.end.getTime() - timeline.timelineBounds.start.getTime();
    
    if (isWindowLocked && windowSizeMs !== null) {
      // Calculate midpoint of the selected range
      const midPercentage = (start + end) / 2;
      const midpointTime = timeline.timelineBounds.start.getTime() + (totalDuration * midPercentage / 100);
      
      // Calculate half of the window size
      const halfWindow = windowSizeMs / 2;
      
      // Calculate new start and end times based on the midpoint and fixed window size
      let newStart = new Date(midpointTime - halfWindow);
      let newEnd = new Date(midpointTime + halfWindow);
      
      // Prevent going beyond timeline bounds
      if (newStart < timeline.timelineBounds.start) {
        // If we would go past the left bound, anchor to the left bound
        newStart = new Date(timeline.timelineBounds.start);
        newEnd = new Date(timeline.timelineBounds.start.getTime() + windowSizeMs);
      } else if (newEnd > timeline.timelineBounds.end) {
        // If we would go past the right bound, anchor to the right bound
        newEnd = new Date(timeline.timelineBounds.end);
        newStart = new Date(timeline.timelineBounds.end.getTime() - windowSizeMs);
      }
      
      timeline.updateVisibleRange(newStart, newEnd);
            } else {
      // Original behavior - range can be adjusted freely
      const newStart = new Date(timeline.timelineBounds.start.getTime() + (totalDuration * start / 100));
      const newEnd = new Date(timeline.timelineBounds.start.getTime() + (totalDuration * end / 100));
      
      timeline.updateVisibleRange(newStart, newEnd);
    }
  }, [timeline, isWindowLocked, windowSizeMs]);

  // Handle window lock toggle
  const handleWindowLockToggle = useCallback(() => {
    // Don't allow locking when at minimum zoom
    if (isMinZoom) return;
    
    if (!isWindowLocked) {
      // Locking the window - store current window size
      const currentWindowSize = timeline.viewState.visibleTimeRange.end.getTime() - 
        timeline.viewState.visibleTimeRange.start.getTime();
      setWindowSizeMs(currentWindowSize);
    } else {
      // Unlocking the window - clear stored size
      setWindowSizeMs(null);
    }
    setIsWindowLocked(!isWindowLocked);
  }, [isWindowLocked, timeline.viewState.visibleTimeRange, isMinZoom]);

  // Handle item click
  const handleItemClick = useCallback((item: TimelineItemType) => {
    // For work experiences, zoom to their date range
    if (item.type === 'workExperience') {
      const startDate = new Date(item.startDate);
      const endDate = item.endDate ? new Date(item.endDate) : new Date();
      
      // Add padding to the date range (1 month before, 1 month after)
      const paddedStartDate = new Date(startDate);
      paddedStartDate.setMonth(paddedStartDate.getMonth() - 1);
      
      const paddedEndDate = new Date(endDate);
      paddedEndDate.setMonth(paddedEndDate.getMonth() + 1);
      
      // Calculate the range duration in milliseconds
      const rangeMs = paddedEndDate.getTime() - paddedStartDate.getTime();
      
      // Ensure minimum range is 4 weeks (28 days)
      const fourWeeksMs = 28 * 24 * 60 * 60 * 1000;
      
      if (rangeMs < fourWeeksMs) {
        // If range is less than 4 weeks, center the midpoint and extend to 4 weeks
        const midpoint = (paddedStartDate.getTime() + paddedEndDate.getTime()) / 2;
        const newStartDate = new Date(midpoint - fourWeeksMs / 2);
        const newEndDate = new Date(midpoint + fourWeeksMs / 2);
        
        timeline.updateVisibleRange(newStartDate, newEndDate);
        } else {
        timeline.updateVisibleRange(paddedStartDate, paddedEndDate);
      }
      
      return;
    }

    // Navigate to appropriate page based on item type for non-work experience items
            switch (item.type) {
      case 'careerCard':
                    navigate('/home', { 
                        state: { 
            openCardId: item.id,
            shouldOpenCard: true,
                            shouldScrollIntoView: true
                        } 
                    });
                    break;
      case 'note':
                    navigate('/home', { 
                        state: { 
            openNoteId: item.id,
            shouldOpenNote: true,
                            shouldScrollIntoView: true
                        } 
                    });
                    break;
                case 'review':
                    navigate('/home', { 
                        state: { 
                            openReviewId: item.id,
                            shouldOpenReview: true,
                            shouldScrollIntoView: true
                        } 
                    });
                    break;
                case 'retrospective':
                    navigate(`/retrospectives/${item.id}`);
                    break;
      case 'resume':
      case 'file':
        navigate('/files', {
          state: {
            openFileId: item.id,
            shouldOpenFile: true
          }
        });
        break;
                        default:
        // No action for unknown types
        break;
    }
  }, [navigate, timeline]);

  // Handle hover
  const handleItemHover = useCallback((item: TimelineItemType | null, position?: HoverPosition) => {
    setHoveredItemData({
      item,
      position: position || null
    });
    }, []);

  // Handle zoom controls
  const handleZoomIn = useCallback(() => {
    timeline.updateZoom(Math.min(timeline.viewState.zoomLevel + 10, 100));
  }, [timeline]);

  const handleZoomOut = useCallback(() => {
    timeline.updateZoom(Math.max(timeline.viewState.zoomLevel - 10, 0));
  }, [timeline]);

  // Render content based on loading state
  if (timeline.isLoading) {
        return (
      <div className="timeline-container p-4 pt-24 bg-gray-900 text-white h-screen overflow-auto">
        <PageHeader title="Timeline" />
        <div className="flex items-center justify-center h-[500px] mt-6">
          <div className="animate-pulse">Loading timeline data...</div>
            </div>
            </div>
        );
  }

  // If no data
  if (timeline.timelineData.length === 0 && !timeline.isLoading) {
        return (
      <div className="timeline-container p-4 pt-24 bg-gray-900 text-white h-screen overflow-auto">
        <PageHeader title="Timeline" />
        <div className="flex flex-col items-center justify-center h-[500px] mt-6 text-center">
          <h3 className="text-xl font-medium mb-2">No timeline data available</h3>
          <p className="text-gray-300 mb-6">
            Add work experiences, create notes, or upload files to see them on your timeline.
          </p>
                    </div>
            </div>
        );
  }

            return (
    <div className="timeline-container p-4 pt-24 bg-gray-900 text-white h-screen overflow-auto">
      <PageHeader title="Timeline" />
      
      <div className="pb-20">
        {/* Timeline Controls Section */}
        <div className="mb-6">
          {/* Zoom Controls Row */}
          <div className="flex items-center space-x-1 mb-3">
            <TimelineControls
              zoomLevel={timeline.viewState.zoomLevel}
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              onReset={timeline.resetView}
              isWindowLocked={isWindowLocked}
                        />
                    </div>

          {/* Timeline Position/Range Section */}
          <div className="mb-4">
            {/* Date range and lock control - stack on mobile, side-by-side on larger screens */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-2 gap-2">
              {/* Timeline Position text - mobile view */}
              <div className="text-sm text-gray-300 min-h-[24px] min-w-0 sm:min-w-[280px]">
                <div className="whitespace-nowrap overflow-hidden text-ellipsis">
                  Timeline Position: {format(timeline.viewState.visibleTimeRange.start, 'MMM d, yyyy')} - {format(timeline.viewState.visibleTimeRange.end, 'MMM d, yyyy')}
                </div>
                
                {/* Lock window size checkbox - mobile view only */}
                <div className="flex items-center mt-2 sm:hidden">
                  <input
                    type="checkbox"
                    id="window-lock-mobile"
                    checked={isWindowLocked}
                    onChange={handleWindowLockToggle}
                    disabled={isMinZoom}
                    className={`mr-2 h-4 w-4 rounded ${
                      isMinZoom 
                        ? 'opacity-50 cursor-not-allowed text-gray-400 focus:ring-gray-400' 
                        : 'text-blue-600 focus:ring-blue-500'
                    }`}
                  />
                  <label 
                    htmlFor="window-lock-mobile" 
                    className={`text-xs whitespace-nowrap ${
                      isMinZoom 
                        ? 'text-gray-500 cursor-not-allowed' 
                        : 'text-gray-300'
                    }`}
                  >
                    Lock window size
                  </label>
                    </div>
                </div>
              
              {/* Lock window size checkbox - desktop view only */}
              <div className="hidden sm:flex sm:items-center">
                <input
                  type="checkbox"
                  id="window-lock-desktop"
                  checked={isWindowLocked}
                  onChange={handleWindowLockToggle}
                  disabled={isMinZoom}
                  className={`mr-2 h-4 w-4 rounded ${
                    isMinZoom 
                      ? 'opacity-50 cursor-not-allowed text-gray-400 focus:ring-gray-400' 
                      : 'text-blue-600 focus:ring-blue-500'
                  }`}
                />
                <label 
                  htmlFor="window-lock-desktop" 
                  className={`text-xs whitespace-nowrap ${
                    isMinZoom 
                      ? 'text-gray-500 cursor-not-allowed' 
                      : 'text-gray-300'
                  }`}
                >
                  Lock window size
                        </label>
              </div>
            </div>
            
            {/* Range Slider with safe padding on mobile */}
            <div className="px-8 sm:px-0">
                <Range
                    values={timelineRange}
                    step={0.1}
                    min={0}
                    max={100}
                    onChange={(values) => {
                        const roundedValues = values.map(v => 
                            Math.min(Math.max(Math.round(v * 10) / 10, 0), 100));
                        handleTimelineRangeChange(roundedValues);
                    }}
                    aria-label="Adjust timeline position"
                    renderTrack={({ props, children }) => (
                        <div
                            {...{
                                onMouseDown: props.onMouseDown,
                                onTouchStart: props.onTouchStart,
                                style: {
                                height: '44px', // Increased touch target
                                    display: 'flex',
                                    width: '100%',
                                    ...props.style
                                }
                            }}
                        >
                            <div
                                ref={props.ref}
                                style={{
                                height: '6px',
                                    width: '100%',
                                    borderRadius: '4px',
                                    background: getTrackBackground({
                                        values: timelineRange,
                            colors: ['#2c3e50', '#3498db', '#2c3e50'],
                                        min: 0,
                                        max: 100
                                    }),
                                    alignSelf: 'center'
                                }}
                            >
                                {children}
                            </div>
                        </div>
                    )}
                    renderThumb={({ props }) => {
                        const { key, ...otherProps } = props;
                    return (
                      <div 
                        key={key} 
                        {...otherProps} 
                        style={{ 
                          ...props.style, 
                          height: '24px', 
                          width: '24px', 
                          borderRadius: '50%', // Make it circular
                          backgroundColor: '#FFF', 
                          display: 'flex', 
                          justifyContent: 'center', 
                          alignItems: 'center', 
                          boxShadow: '0px 2px 6px rgba(0,0,0,0.3)',
                          touchAction: 'none' // Improves touch handling
                        }} 
                      />
                    );
                    }}
                />
            </div>
            </div>

          {/* Legend/Filter Types */}
          <div className="mb-2">
            <TimelineLegend
              activeTypes={Array.from(timeline.visibleTypes)}
              onToggleType={timeline.toggleItemType}
            />
                </div>
        </div>
        
        {/* Timeline Content */}
        <div 
          className="timeline-content rounded-lg relative bg-white"
          ref={containerRef}
          style={{ height: totalContentHeight, minHeight: '500px' }}
        >
          {/* Main SVG Timeline */}
          {timeline.dimensions.containerWidth > 0 && (
            <svg
              width={timeline.dimensions.containerWidth}
              height={totalContentHeight}
              className="timeline-svg"
            >
              <Group>
                {/* Timeline Grid/Axis */}
                <TimelineGrid
                  startDate={timeline.viewState.visibleTimeRange.start}
                  endDate={timeline.viewState.visibleTimeRange.end}
                  width={timeline.dimensions.containerWidth}
                  height={totalContentHeight}
                  xScale={timeline.xScale}
                />
                
                {/* Work Experience Items */}
                {timeline.visibleData.workExperiences.map((item, index) => (
                  <TimelineItem
                    key={item.id}
                    item={item}
                    xScale={timeline.xScale}
                    yPosition={calculateYPosition(index, 'workExperience')}
                    onItemClick={handleItemClick}
                    onItemHover={handleItemHover}
                    isHovered={hoveredItemData.item?.id === item.id}
                    containerWidth={timeline.dimensions.containerWidth}
                  />
                ))}
                
                {/* Other Items (notes, cards, files, etc.) */}
                {timeline.visibleData.otherItems.map((item) => (
                  <TimelineItem
                    key={item.id}
                    item={item}
                    xScale={timeline.xScale}
                    yPosition={calculateYPosition(0, item.type)}
                    onItemClick={handleItemClick}
                    onItemHover={handleItemHover}
                    isHovered={hoveredItemData.item?.id === item.id}
                    containerWidth={timeline.dimensions.containerWidth}
                  />
                ))}
              </Group>
            </svg>
          )}
          
          {/* Tooltip */}
          {hoveredItemData.item && hoveredItemData.position && (
            <TimelineTooltip
              item={hoveredItemData.item}
              position={hoveredItemData.position}
              containerHeight={timeline.dimensions.containerHeight}
              isVisible={!!hoveredItemData.item}
            />
          )}
        </div>

        {/* Add extra bottom space */}
        <div className="h-16"></div>
            </div>
        </div>
    );
};

export default TimelineView;